// «Ρωτήστε το AVRASTRO» (owner, 16 Oct 2026) — the pure part of the CRM's help assistant: no React, no network.
//   redactQuestion   removes e-mail addresses, phone numbers, identity / policy numbers and IBANs from a question
//                    BEFORE anything leaves the device (the platform scrubs again); says what it removed
//   helpSections     the CRM's own help in one language as sections with a reference (a:<article>, l:<lesson>,
//                    n:<version>) — the only content the assistant answers from; [[key]] labels resolved live
//   pickHelp         the best sections for a question (accent- and case-free, light stems; titles count more)
//   refTarget        where a reference leads: the article, the lesson, «What's new», and the screens the article opens
// Client data never travels: the question (redacted) and these help sections are all that is sent.
import { HELP_ARTICLES, WHATS_NEW, articleText, type Lang3 } from './articles.ts';
import { LESSONS } from './lessons.ts';
import { docKeys, queryKeyGroups } from '@vero/platform';

export interface Redaction { text: string; removed: { email: number; phone: number; iban: number; id: number } }
/** e-mails, IBANs, phone numbers (Cyprus and international), identity / policy / card numbers — replaced by a tag */
export function redactQuestion(input: string): Redaction {
  const removed = { email: 0, phone: 0, iban: 0, id: 0 };
  let t = String(input ?? '');
  const rep = (re: RegExp, kind: keyof Redaction['removed'], tag: string) => { t = t.replace(re, () => { removed[kind]++; return tag; }); };
  rep(/[^\s@<>()"']+@[^\s@<>()"']+\.[a-z]{2,}/gi, 'email', '[e-mail]');
  rep(/\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]{4}){3,7}(?:[ ]?[A-Z0-9]{1,4})?\b/gi, 'iban', '[IBAN]');
  rep(/(?:\+|00)\d{1,3}[ .-]?(?:\d[ .-]?){7,12}\d/g, 'phone', '[tel]');
  rep(/\b(?:2|9)\d[ .-]?\d{6}\b/g, 'phone', '[tel]');
  rep(/\b(?:\d[ -]?){12,18}\d\b/g, 'id', '[#]'); // card-like runs
  rep(/\b\d{6,}\b/g, 'id', '[#]');
  rep(/\b[A-Z]{1,3}[ -]?\d{5,}\b/gi, 'id', '[#]');
  return { text: t, removed };
}
export const removedCount = (r: Redaction) => r.removed.email + r.removed.phone + r.removed.iban + r.removed.id;

// someone's own health («έχω καρκίνο», "I was diagnosed with…", «у меня диабет»): never sent — answered on the device
const HEALTH = /(^|[^\p{L}])(εχω|επαθα|πασχει|πασχω|διαγνωσ\p{L}*|i have|i've got|was diagnosed|diagnosed with|has|у меня|болеет|болен|больна)[^\p{L}].{0,40}(καρκιν|διαβητ|ζαχαρ|καρδι|εγκεφαλ|καταθλιψ|ογκ|hiv|cancer|diabet|tumou?r|heart|stroke|depression|surgery|χειρουργ|εγκυ|pregnan|рак|диабет|сердц|инсульт|депресс|опухол|операц|беремен)/u;
export const mentionsOwnHealth = (text: string) => HEALTH.test(fold(text));

export interface HelpSection { ref: string; title: string; text: string }
const fold = (s: string) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ς/g, 'σ').replace(/ё/g, 'е');
const STOP = new Set(['και', 'για', 'που', 'μια', 'ενα', 'στο', 'στη', 'στην', 'στον', 'στα', 'του', 'τησ', 'των', 'τον', 'την', 'τα', 'το', 'οι', 'με', 'σε', 'απο', 'αν', 'να', 'θα', 'δεν', 'μη', 'μην', 'τι', 'πωσ', 'ποιο', 'ποια', 'ειναι', 'εχω', 'εχει', 'μου', 'σασ', 'μασ', 'κανω', 'μπορω', 'οταν', 'avrastro', 'crm',
  'the', 'and', 'for', 'what', 'how', 'does', 'are', 'can', 'my', 'of', 'to', 'in', 'on', 'it', 'or', 'with', 'you', 'your', 'is', 'do', 'an', 'when', 'which', 'this', 'that', 'there', 'have', 'has', 'where',
  'и', 'в', 'во', 'на', 'с', 'со', 'по', 'для', 'что', 'как', 'это', 'мне', 'мой', 'вы', 'вас', 'ваш', 'ли', 'или', 'если', 'не', 'от', 'до', 'из', 'за', 'о', 'у', 'где', 'когда', 'какои', 'можно', 'нужно']);
/** the keys of a text's meaningful words (17 Oct 2026: phonetic keys shared with the platform — @vero/platform text-normalise) */
export function helpStems(text: string): string[] {
  return [...docKeys(text, STOP)];
}

/** the CRM help in one language: one section per article, lesson and release (texts with the live labels) */
export function helpSections(lang: Lang3, label: (key: string) => string = (k) => k): HelpSection[] {
  const res = (s: string) => s.replace(/\[\[([a-z0-9_.]+)\]\]/gi, (_, k: string) => `«${label(k)}»`);
  const cut = (s: string) => (s.length > 1400 ? s.slice(0, 1400) + ' …' : s);
  const out: HelpSection[] = [];
  for (const a of HELP_ARTICLES) out.push({ ref: `a:${a.id}`, title: a.title[lang], text: cut(articleText(a, lang, (k) => `«${label(k)}»`).replace(/\s*\n\s*/g, ' ')) });
  for (const l of LESSONS) out.push({ ref: `l:${l.slug}`, title: l.title[lang], text: cut(res([l.goal[lang], ...l.steps.map((x) => x[lang]), ...(l.tips ?? []).map((x) => x[lang])].join(' '))) });
  for (const r of WHATS_NEW) out.push({ ref: `n:${r.version}`, title: `${{ el: 'Τι νέο υπάρχει', en: "What's new", ru: 'Что нового' }[lang]} ${r.version}`, text: cut(r.items.map((x) => x[lang]).join(' ')) });
  return out;
}

/** the best sections for a question (none below the bar; at most n) */
export function pickHelp(question: string, sections: readonly HelpSection[], n = 4): (HelpSection & { score: number })[] {
  // 17 Oct 2026: the question as people type it — Greeklish, no accents, Cypriot, Latin-letter Russian — as groups of
  // alternative keys (one per word); a section matches a word when it has any of them
  const q = queryKeyGroups(question, STOP);
  if (!q.length) return [];
  const need = q.length <= 2 ? 1 : 2;
  const scored: (HelpSection & { score: number })[] = [];
  for (const s of sections) {
    const t = docKeys(s.title, STOP), b = docKeys(s.text, STOP);
    let score = 0;
    for (const g of q) score += g.some((w) => t.has(w)) ? 3 : g.some((w) => b.has(w)) ? 1 : 0;
    if (score >= need) scored.push({ ...s, score });
  }
  return scored.sort((a, b) => b.score - a.score || a.text.length - b.text.length).slice(0, n);
}

export interface RefTarget { kind: 'article' | 'lesson' | 'new'; id: string; path: string; screens: { path: string; label: string }[] }
/** where a cited reference leads in the app (null for an unknown one) */
export function refTarget(ref: string): RefTarget | null {
  const [k, id = ''] = ref.split(':');
  if (k === 'a') { const a = HELP_ARTICLES.find((x) => x.id === id); return a ? { kind: 'article', id, path: `/help?a=${id}`, screens: a.open ?? [] } : null; }
  if (k === 'l') { const l = LESSONS.find((x) => x.slug === id); return l ? { kind: 'lesson', id, path: `/lessons/${id}`, screens: l.practice ? [l.practice] : [] } : null; }
  if (k === 'n') return WHATS_NEW.some((r) => r.version === id) ? { kind: 'new', id, path: '/help?tab=new', screens: [] } : null;
  return null;
}
/** the title of a reference in a language */
export function refTitle(ref: string, lang: Lang3): string {
  const [k, id = ''] = ref.split(':');
  if (k === 'a') return HELP_ARTICLES.find((x) => x.id === id)?.title[lang] ?? ref;
  if (k === 'l') return LESSONS.find((x) => x.slug === id)?.title[lang] ?? ref;
  return `${{ el: 'Τι νέο υπάρχει', en: "What's new", ru: 'Что нового' }[lang]} ${id}`;
}
